import { FC, useState } from 'react'
import { useIntl } from 'react-intl'
import { GuideSystem, guideLanguage, guideSystem } from './model/extend-guide'
import {
	ExtendScreensGuide,
	GuidePictures,
} from './presentation/extend-screens-guide'
import macKeysEn from './presentation/images/extend-screens/mac-keys-en.png'
import macKeysPl from './presentation/images/extend-screens/mac-keys-pl.png'
import macSettingsEn from './presentation/images/extend-screens/mac-settings-en.png'
import macSettingsPl from './presentation/images/extend-screens/mac-settings-pl.png'
import winKeysEn from './presentation/images/extend-screens/win-keys-en.png'
import winKeysPl from './presentation/images/extend-screens/win-keys-pl.png'
import winSettingsEn from './presentation/images/extend-screens/win-settings-en.png'
import winSettingsPl from './presentation/images/extend-screens/win-settings-pl.png'
import { usePresenterConsole } from './presenter-console'
import { useExtendedDisplay } from './use-extended-display'

/**
 * The pictures show the systems' own screens, labelled as each system labels
 * them in that language.
 */
const PICTURES: Record<'en' | 'pl', Record<GuideSystem, GuidePictures>> = {
	en: {
		mac: { keys: macKeysEn, settings: macSettingsEn },
		windows: { keys: winKeysEn, settings: winSettingsEn },
	},
	pl: {
		mac: { keys: macKeysPl, settings: macSettingsPl },
		windows: { keys: winKeysPl, settings: winSettingsPl },
	},
}

type WithUserAgentData = Navigator & {
	userAgentData?: { platform?: string }
}

const platformName = (): string | undefined =>
	(navigator as WithUserAgentData).userAgentData?.platform || navigator.platform

/**
 * The guide to extending the screens, while the console's Present found them
 * mirrored (`guide` in `model/presentation-machine.ts`). It watches the
 * screens while open; its Present goes on with the ordinary presenting.
 */
export const ConfiguredExtendScreensGuide: FC = () => {
	const presenter = usePresenterConsole()
	const open = presenter?.state.phase === 'guide'
	const extended = useExtendedDisplay(open) === true
	const { locale } = useIntl()
	const [system, setSystem] = useState(() => guideSystem(platformName()))

	if (!presenter || !open) {
		return null
	}
	return (
		<ExtendScreensGuide
			system={system}
			onSystem={setSystem}
			pictures={PICTURES[guideLanguage(locale)]}
			extended={extended}
			onPresent={presenter.present}
			onPresentHere={presenter.presentHere}
			onClose={presenter.end}
		/>
	)
}
